import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, resolveArtworkUrl } from "@/lib/api";
import { Loader2, Sparkles, Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import DiscogsSearchDialog from "@/components/DiscogsSearchDialog";

const VENDORS = ["Juno", "RareVinyl", "Chalkys", "The Sound of Vinyl", "Other"];
const PRIORITIES = ["Must have", "Nice to have"];

const blank = {
    artist: "", album_title: "", release_year: "", label: "", catalog_number: "",
    genre: "", notes: "", target_price: "", priority: "Nice to have",
    artwork_url: "", discogs_id: null, discogs_url: "",
    vendors: [],
};

export default function WishlistDialog({ open, onOpenChange, onSave, initial }) {
    const [form, setForm] = useState(blank);
    const [saving, setSaving] = useState(false);
    const [discogsOpen, setDiscogsOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (initial) {
            setForm({
                ...blank,
                ...initial,
                release_year: initial.release_year ?? "",
                target_price: initial.target_price ?? "",
                vendors: Array.isArray(initial.vendors) ? initial.vendors : [],
            });
        } else {
            setForm(blank);
        }
    }, [open, initial]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleDiscogsPick = (d) => {
        setForm(f => ({
            ...f,
            artist: d.artist || f.artist,
            album_title: d.album_title || f.album_title,
            release_year: d.release_year ? String(d.release_year) : f.release_year,
            label: d.label || f.label,
            catalog_number: d.catalog_number || f.catalog_number,
            genre: d.genre || f.genre,
            artwork_url: d.cover_image || f.artwork_url,
            discogs_id: d.discogs_id || f.discogs_id,
            discogs_url: d.discogs_url || f.discogs_url,
        }));
    };

    const addVendor = () => set("vendors", [...form.vendors, { vendor: "Juno", url: "", price: "" }]);
    const removeVendor = (idx) => set("vendors", form.vendors.filter((_, i) => i !== idx));
    const updateVendor = (idx, k, v) => {
        const copy = form.vendors.slice();
        copy[idx] = { ...copy[idx], [k]: v };
        set("vendors", copy);
    };

    const handleSubmit = async () => {
        if (!form.artist.trim() || !form.album_title.trim()) {
            toast.error("Artist and album title are required");
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            release_year: form.release_year ? parseInt(form.release_year, 10) : null,
            target_price: form.target_price !== "" ? parseFloat(form.target_price) : null,
            label: form.label || null,
            catalog_number: form.catalog_number || null,
            genre: form.genre || null,
            notes: form.notes || null,
            artwork_url: form.artwork_url || null,
            discogs_id: form.discogs_id || null,
            discogs_url: form.discogs_url || null,
            vendors: form.vendors.map(v => ({
                vendor: v.vendor || "Other",
                url: v.url || null,
                price: v.price !== "" && v.price != null ? parseFloat(v.price) : null,
                label: v.label || null,
            })),
        };
        ["id", "created_at", "updated_at"].forEach(k => delete payload[k]);
        await onSave(payload, initial?.id);
        setSaving(false);
    };

    const preview = form.artwork_url ? resolveArtworkUrl({ artwork_url: form.artwork_url }) : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="wishlist-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl font-bold tracking-tight">
                        {initial ? "Edit wishlist entry" : "Add to wishlist"}
                    </DialogTitle>
                    <DialogDescription>
                        Records you&apos;re hunting — with vendor links and a target price.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-secondary/60 border border-border">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Sparkles className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-foreground/80 truncate">
                            Auto-fill from Discogs — artist, album, year, label, artwork.
                        </p>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDiscogsOpen(true)}
                        className="shrink-0 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                        data-testid="wishlist-open-discogs-btn"
                    >
                        Search Discogs
                    </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6 py-2">
                    <div className="space-y-3">
                        <Label className="overline">Artwork preview</Label>
                        <div className="aspect-square rounded-md border border-border bg-secondary overflow-hidden flex items-center justify-center">
                            {preview ? (
                                <img src={preview} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <p className="text-sm text-muted-foreground p-6 text-center">No artwork yet</p>
                            )}
                        </div>
                        <div>
                            <Label className="overline">Artwork URL</Label>
                            <Input
                                value={form.artwork_url || ""}
                                onChange={(e) => set("artwork_url", e.target.value)}
                                placeholder="Auto-filled from Discogs or paste URL"
                                className="mt-1.5"
                                data-testid="wishlist-artwork-url"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label className="overline">Artist *</Label>
                            <Input value={form.artist} onChange={(e) => set("artist", e.target.value)} className="mt-1.5" data-testid="wishlist-artist" />
                        </div>
                        <div>
                            <Label className="overline">Album title *</Label>
                            <Input value={form.album_title} onChange={(e) => set("album_title", e.target.value)} className="mt-1.5" data-testid="wishlist-album" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Priority</Label>
                                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="wishlist-priority"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">Target price</Label>
                                <Input type="number" step="0.01" value={form.target_price} onChange={(e) => set("target_price", e.target.value)} className="mt-1.5" data-testid="wishlist-target-price" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Release year</Label>
                                <Input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className="mt-1.5" data-testid="wishlist-year" />
                            </div>
                            <div>
                                <Label className="overline">Label</Label>
                                <Input value={form.label} onChange={(e) => set("label", e.target.value)} className="mt-1.5" data-testid="wishlist-label" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Catalog #</Label>
                                <Input value={form.catalog_number} onChange={(e) => set("catalog_number", e.target.value)} className="mt-1.5" data-testid="wishlist-catalog" />
                            </div>
                            <div>
                                <Label className="overline">Genre</Label>
                                <Input value={form.genre} onChange={(e) => set("genre", e.target.value)} className="mt-1.5" data-testid="wishlist-genre" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                        <Label className="overline">Vendors</Label>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={addVendor}
                            className="gap-1.5"
                            data-testid="wishlist-add-vendor-btn"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add vendor
                        </Button>
                    </div>
                    {form.vendors.length === 0 && (
                        <p className="text-sm text-muted-foreground italic">No vendors yet — add one to track where to buy this.</p>
                    )}
                    {form.vendors.map((v, idx) => (
                        <div key={idx} className="grid grid-cols-[140px_1fr_100px_auto] gap-2 items-start" data-testid={`wishlist-vendor-row-${idx}`}>
                            <Select value={v.vendor || "Other"} onValueChange={(val) => updateVendor(idx, "vendor", val)}>
                                <SelectTrigger data-testid={`wishlist-vendor-select-${idx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VENDORS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <Input
                                value={v.url || ""}
                                onChange={(e) => updateVendor(idx, "url", e.target.value)}
                                placeholder="https://…"
                                data-testid={`wishlist-vendor-url-${idx}`}
                            />
                            <Input
                                type="number"
                                step="0.01"
                                value={v.price ?? ""}
                                onChange={(e) => updateVendor(idx, "price", e.target.value)}
                                placeholder="Price"
                                data-testid={`wishlist-vendor-price-${idx}`}
                            />
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeVendor(idx)}
                                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                data-testid={`wishlist-vendor-remove-${idx}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    ))}
                </div>

                <div className="pt-2">
                    <Label className="overline">Notes</Label>
                    <Textarea
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        placeholder="Pressing details, condition preferences, alternates…"
                        rows={3}
                        className="mt-1.5"
                        data-testid="wishlist-notes"
                    />
                </div>

                <DialogFooter className="pt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wishlist-cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="wishlist-save-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {initial ? "Save changes" : "Add to wishlist"}
                    </Button>
                </DialogFooter>
            </DialogContent>
            <DiscogsSearchDialog
                open={discogsOpen}
                onOpenChange={setDiscogsOpen}
                onPick={handleDiscogsPick}
            />
        </Dialog>
    );
}
